import React, { useEffect, useState } from 'react';
import { API_BASE } from '../config';

export default function StravaDebug() {
  const [activities, setActivities] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [normalized, setNormalized] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingOne, setLoadingOne] = useState(false);
  const [error, setError] = useState('');

  const loadRecent = async () => {
    setLoadingList(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/strava/activities/recent?limit=5`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to fetch Strava activities');
      setActivities(data.activities || []);
      setSelectedId((data.activities?.[0]?.id) || null);
    } catch (e) {
      setError(e?.message || 'Failed to fetch Strava activities');
    } finally {
      setLoadingList(false);
    }
  };

  const loadNormalized = async (id) => {
    if (!id) return;
    setLoadingOne(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/strava/activities/${id}/normalized`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      console.log('data', data);
      if (!res.ok) throw new Error(data?.error || 'Failed to fetch activity details');
      setNormalized(data);
    } catch (e) {
      setError(e?.message || 'Failed to fetch activity details');
      setNormalized(null);
    } finally {
      setLoadingOne(false);
    }
  };

  useEffect(() => {
    loadRecent();
  }, []);

  useEffect(() => {
    if (selectedId) loadNormalized(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-lg mt-10">
      <h1 className="text-3xl font-bold text-gray-800 mb-2">Strava Debug</h1>
      <p className="text-gray-600 mb-6">Fetch the last 5 activities, then fetch and display the normalized JSON used by the coach.</p>

      <div className="flex justify-between items-center mb-4">
        <button
          onClick={loadRecent}
          disabled={loadingList}
          className={`px-4 py-2 rounded-lg font-bold ${loadingList ? 'bg-gray-300 text-gray-500' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
        >
          {loadingList ? 'Loading…' : 'Refresh'}
        </button>
        {error ? <div className="text-sm text-red-700">{error}</div> : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="font-bold text-gray-700 mb-3">Recent activities</div>
          {activities.length === 0 ? (
            <div className="text-sm text-gray-500">No activities.</div>
          ) : (
            <div className="space-y-2">
              {activities.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setSelectedId(a.id)}
                  className={`w-full text-left p-3 rounded-lg border transition ${
                    selectedId === a.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-bold text-gray-900">{a.name || `Activity ${a.id}`}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {(a.distance ? (a.distance / 1000).toFixed(2) : '?')} km • {a.moving_time ? `${Math.round(a.moving_time / 60)} min` : '?'}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <div className="font-bold text-gray-700 mb-3">Normalized JSON</div>
          {loadingOne ? (
            <div className="text-sm text-gray-500">Loading activity…</div>
          ) : normalized ? (
            <details className="bg-gray-900 rounded-lg p-4" open>
              <summary className="text-gray-400 font-mono text-sm cursor-pointer mb-2">View Raw JSON</summary>
              <pre className="text-green-400 text-xs overflow-x-auto">{JSON.stringify(normalized, null, 2)}</pre>
            </details>
          ) : (
            <div className="text-sm text-gray-500">Select an activity.</div>
          )}
        </div>
      </div>
    </div>
  );
}


